import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAuthToken, formatApiErrorDetail } from "@/api/client";

const AuthCtx = createContext(null);

function applyDarkClass(on) {
    const root = document.documentElement;
    if (on) root.classList.add("dark");
    else root.classList.remove("dark");
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
    const [loading, setLoading] = useState(true);
    const [dark, setDark] = useState(() => {
        try { return localStorage.getItem("exprivia_dark") === "1"; } catch { return false; }
    });

    // Apply dark mode class on the <html> element whenever it changes.
    useEffect(() => { applyDarkClass(dark); }, [dark]);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            // Sync dark_mode from server preference (server wins on first fetch).
            if (typeof data.dark_mode === "boolean") {
                setDark(data.dark_mode);
                try { localStorage.setItem("exprivia_dark", data.dark_mode ? "1" : "0"); } catch { /* */ }
            }
        } catch {
            setUser(false);
            setAuthToken(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) setAuthToken(data.access_token);
            await fetchMe();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setAuthToken(null);
        setUser(false);
    };

    const toggleDark = async () => {
        const next = !dark;
        setDark(next);
        try { localStorage.setItem("exprivia_dark", next ? "1" : "0"); } catch { /* */ }
        // Persist to backend (fire-and-forget; local state already applied)
        try { await api.put("/auth/preferences", { dark_mode: next }); } catch { /* keep local */ }
    };

    const hasPerm = (perm) => Array.isArray(user?.permissions) && user.permissions.includes(perm);
    const isAdmin = () => user?.role === "administrator";
    const isApprover = () => Boolean(user?.can_approve_order_awards) || isAdmin();

    return (
        <AuthCtx.Provider value={{ user, loading, login, logout, refresh: fetchMe, hasPerm, isAdmin, isApprover, dark, toggleDark }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
